import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Eye } from "lucide-react";
import { GetResourceSearchResultsItem } from "@/api/getResourceSearchResults/types";
import { useAppDispatch } from "@/app/hooks";
import { setScrollPosition } from "@/modules/hub/features/ResourceLibrary/slices/resourceLibrarySlice";
import { FavoriteButton } from "@/modules/hub/features/ResourceLibrary/components/FavoriteButton/FavoriteButton";
import { toggleResourceFavorite } from "@/api/favoriteResource/favoriteResourceApi";
import { trackResourceCardClick } from "@/modules/hub/features/ResourceLibrary/analytics/resourceLibraryAnalytics";
import { trackPodcastCardClick } from "@/modules/hub/features/Podcasts/analytics/podcastsAnalytics";
import { trackNewsCardClick } from "@/modules/hub/features/News/analytics/newsAnalytics";

interface MinimalResourceCardProps {
  resource: GetResourceSearchResultsItem;
  onToggleFavorite?: (uuid: string, isFavorited: boolean) => void;
}

export function MinimalResourceCard({
  resource,
  onToggleFavorite,
}: MinimalResourceCardProps) {
  const dispatch = useAppDispatch();
  const [isFavorited, setIsFavorited] = useState(resource.isFavorited ?? false);

  const contentType = (resource.resourceType || "document").toLowerCase();

  const handleCardClick = () => {
    const scrollPosition =
      window.scrollY ||
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop;

    dispatch(setScrollPosition(scrollPosition));

    if (resource.resourceType === "Podcast") {
      trackPodcastCardClick({
        resourceType: resource.resourceType,
        resourceUuid: resource.uuid,
      });
    } else if (resource.resourceType === "News") {
      trackNewsCardClick({
        resourceType: resource.resourceType,
        resourceUuid: resource.uuid,
      });
    } else {
      trackResourceCardClick({
        resourceType: resource.resourceType || "Unknown",
        resourceUuid: resource.uuid,
      });
    }
  };

  const handleToggleFavorite = async (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const previousState = isFavorited;
    setIsFavorited(!isFavorited);

    try {
      await toggleResourceFavorite({ resourceUuid: resource.uuid });
      if (onToggleFavorite) {
        onToggleFavorite(resource.uuid, !previousState);
      }
    } catch (error) {
      setIsFavorited(previousState);
      throw error;
    }
  };

  const isPartnerPage =
    resource.resourceType === "Partner Page" && resource.contentUrl;

  return (
    <div className="block group rounded-md border border-gray-100 dark:border-gray-800 hover:shadow-sm transition-all duration-300 bg-white dark:bg-gray-800 relative">
      {/* Favorite Button */}
      <div className="absolute top-2 left-2" style={{ zIndex: 1 }}>
        <FavoriteButton
          isFavorited={isFavorited}
          onToggle={handleToggleFavorite}
        />
      </div>

      {isPartnerPage ? (
        <a
          className="block"
          href={resource.contentUrl || ""}
          rel="noopener noreferrer"
          target="_blank"
        >
          <div className="relative aspect-video">
            {resource.thumbnailUrl ? (
              <img
                alt={resource.title}
                className="object-cover rounded-t-md w-full h-full"
                src={resource.thumbnailUrl}
              />
            ) : resource.resourceType === "File" ? (
              <div className="w-full h-full rounded-t-md flex items-center justify-center bg-gradient-to-b from-red-300 to-red-400 dark:from-red-400 dark:to-red-600 relative overflow-hidden">
                <svg
                  className="absolute inset-0 w-full h-full opacity-20"
                  preserveAspectRatio="none"
                  viewBox="0 0 100 100"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0,30 Q25,20 50,30 T100,30 L100,100 L0,100 Z"
                    fill="white"
                  />
                  <path
                    d="M0,60 Q25,50 50,60 T100,60 L100,100 L0,100 Z"
                    fill="white"
                    opacity="0.5"
                  />
                  <path
                    d="M0,85 Q25,80 50,85 T100,85 L100,100 L0,100 Z"
                    fill="white"
                    opacity="0.3"
                  />
                </svg>
                <span
                  className="inline-block relative z-10"
                  dangerouslySetInnerHTML={{
                    __html: resource.primaryIcon ?? "",
                  }}
                  style={{
                    filter: "brightness(0) invert(1)",
                    opacity: 0.9,
                  }}
                />
              </div>
            ) : (
              <div className="w-full h-full rounded-t-md flex items-center justify-center bg-gray-100 dark:bg-gray-700">
                <span
                  className="inline-block"
                  dangerouslySetInnerHTML={{
                    __html: resource.primaryIcon ?? "",
                  }}
                />
              </div>
            )}
          </div>

          <div className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <span
                className="text-xs font-light px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: resource.backgroundColor ?? "#ccc",
                  color: resource.textColor ?? "#000",
                  border: resource.borderColor
                    ? `1px solid ${resource.borderColor}`
                    : "none",
                }}
              >
                {contentType.charAt(0).toUpperCase() + contentType.slice(1)}
              </span>
            </div>

            <h3 className="font-medium text-sm text-gray-900 dark:text-white">
              {resource.title}
            </h3>

            <div className="mt-3 text-xs text-gray-500 font-light">
              <span className="flex items-center gap-1">
                <Eye className="h-3 w-3" />
                {resource.viewCount} views
              </span>
            </div>
          </div>
        </a>
      ) : (
        <Link
          className="block"
          onClick={handleCardClick}
          to={`/hub/resources/${resource.slug}`}
        >
          <div className="relative aspect-video">
            {resource.thumbnailUrl ? (
              <img
                alt={resource.title}
                className="object-cover rounded-t-md w-full h-full"
                src={resource.thumbnailUrl}
              />
            ) : resource.resourceType === "File" ? (
              <div className="w-full h-full rounded-t-md flex items-center justify-center bg-gradient-to-b from-red-300 to-red-400 dark:from-red-400 dark:to-red-600 relative overflow-hidden">
                <svg
                  className="absolute inset-0 w-full h-full opacity-20"
                  preserveAspectRatio="none"
                  viewBox="0 0 100 100"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0,30 Q25,20 50,30 T100,30 L100,100 L0,100 Z"
                    fill="white"
                  />
                  <path
                    d="M0,60 Q25,50 50,60 T100,60 L100,100 L0,100 Z"
                    fill="white"
                    opacity="0.5"
                  />
                  <path
                    d="M0,85 Q25,80 50,85 T100,85 L100,100 L0,100 Z"
                    fill="white"
                    opacity="0.3"
                  />
                </svg>
                <span
                  className="inline-block relative z-10"
                  dangerouslySetInnerHTML={{
                    __html: resource.primaryIcon ?? "",
                  }}
                  style={{
                    filter: "brightness(0) invert(1)",
                    opacity: 0.9,
                  }}
                />
              </div>
            ) : (
              <div className="w-full h-full rounded-t-md flex items-center justify-center bg-gray-100 dark:bg-gray-700">
                <span
                  className="inline-block"
                  dangerouslySetInnerHTML={{
                    __html: resource.primaryIcon ?? "",
                  }}
                />
              </div>
            )}
          </div>

          <div className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <span
                className="text-xs font-light px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: resource.backgroundColor ?? "#ccc",
                  color: resource.textColor ?? "#000",
                  border: resource.borderColor
                    ? `1px solid ${resource.borderColor}`
                    : "none",
                }}
              >
                {contentType.charAt(0).toUpperCase() + contentType.slice(1)}
              </span>
            </div>

            <h3 className="font-medium text-sm text-gray-900 dark:text-white line-clamp-2">
              {resource.title}
            </h3>

            <div className="mt-3 text-xs text-gray-500 font-light">
              <span className="flex items-center gap-1">
                <Eye className="h-3 w-3" />
                {resource.viewCount} views
              </span>
            </div>
          </div>
        </Link>
      )}
    </div>
  );
}
